import React, { useEffect, useState } from 'react'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import {
  SubscriptionDetailsDto,
  SubscriptionPlatformDto,
} from 'api/Trading-Accounts/Types/SubscriptionDetailsDto'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { isEmpty } from 'utils/is-empty'

import { subscriptionPlatformColumns } from './subscriptionPlatformColumns'
import { SubscriptionPlatformValues, SubscriptionsPlatformForm } from './SubscriptionsPlatformForm'

interface SubscriptionsDetailsMainProps {
  subscriptionDetails: SubscriptionDetailsDto
  onSubscriptionAddModalOpen(): void
  onSubscriptionEditModalOpen(data: SubscriptionPlatformDto): void
  onSubscriptionDeleteModalOpen(data: SubscriptionPlatformDto): void
}

export const SubscriptionPlatformsTable: React.FC<SubscriptionsDetailsMainProps> = (props) => {
  const { subscriptionDetails, onSubscriptionDeleteModalOpen } = props

  return (
    <div>
      <Grid resizable data={subscriptionDetails.platforms} pageable style={{ zIndex: 0 }}>
        {subscriptionPlatformColumns
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
        <Column
          width={60}
          title=''
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Delete',
                    'data-test': 'delete',
                    onClick: () => onSubscriptionDeleteModalOpen(gridCellProps.dataItem),
                  },
                ]}
                data-test='more-button'
              />
            </td>
          )}
        />
      </Grid>
    </div>
  )
}

const useMapStateToProps = () => ({
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
  }
}

interface OuterProps {
  subscription: SubscriptionDetailsDto
  onRefresh(): void
}

export const SubscriptionsPlatformPage: React.FC<OuterProps> = (props) => {
  const { subscription: subscriptionDetails, onRefresh } = props

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [subscription, setSubscription] = useState<SubscriptionPlatformDto>()
  const [platforms, setPlatforms] = useState<NameDto[]>([])
  const [isSubscriptionAddModalOpen, setSubscriptionAddModalOpen] = useState<boolean>(false)
  const [isSubscriptionDeleteModalOpen, setSubscriptionDeleteModalOpen] = useState<boolean>(false)

  const { tickmillCompanies } = useMapStateToProps()
  const { fetchTickmillCompanies } = useMapDispatchToProps()

  useEffect(() => {
    if (isEmpty(tickmillCompanies)) fetchTickmillCompanies()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitForm = async (data: SubscriptionPlatformValues) => {
    if (subscriptionDetails) {
      await tradingAccountsApiClient.createTradingAccountSubscriptionPlatform({
        subscriptionId: data?.subscriptionId || subscriptionDetails.id,
        platformTypeId: data.platformType,
        tickmillCompanyId: data?.tickmillCompany,
      })
      setSubscription(undefined)
      setSubscriptionAddModalOpen(false)
      await onRefresh()
    }
  }

  const handleSubscriptionAddModalOpen = () => {
    setSubscriptionAddModalOpen(true)
  }

  const handleSubscriptionAddModalClose = () => {
    setSubscription(undefined)
    setSubscriptionAddModalOpen(false)
  }

  const handleSubscriptionEditModalOpen = (data: SubscriptionPlatformDto) => {
    setSubscription(data)
  }

  const handleSubscriptionDeleteModalOpen = (data: SubscriptionPlatformDto) => {
    setSubscription(data)
    setSubscriptionDeleteModalOpen(true)
  }

  const handleSubscriptionDeleteModalClose = () => {
    setSubscription(undefined)
    setSubscriptionDeleteModalOpen(false)
  }

  const handleSubscriptionDelete = async () => {
    if (subscription) {
      await tradingAccountsApiClient.deleteTradingAccountSubscriptionPlatformById(
        subscription.platformType.id,
        subscription.subscriptionId
      )
      await onRefresh()
      setSubscription(undefined)
      setSubscriptionDeleteModalOpen(false)
    }
  }

  const handleTradingAccountSubscriptionPlatform = async (tickmillCompanyId: number) => {
    const platformTypes =
      await tradingAccountsApiClient.getPlatformTypesCompanyById(tickmillCompanyId)
    setPlatforms(platformTypes)
  }

  return (
    <React.Fragment>
      {isSubscriptionAddModalOpen && (
        <Modal
          width={600}
          visible={isSubscriptionAddModalOpen}
          onClose={handleSubscriptionAddModalClose}
        >
          <SubscriptionsPlatformForm
            subscription={subscription}
            tickmillCompanies={tickmillCompanies}
            platforms={platforms}
            onSubscriptionPlatform={handleTradingAccountSubscriptionPlatform}
            onSubmit={handleSubmitForm}
          />
        </Modal>
      )}
      {isSubscriptionDeleteModalOpen && (
        <ConfirmationModal
          contentText={`Are you sure you want to delete the following information from the subscription? Platform: ${subscription?.platformType.name}, Tickmill Company: ${subscription?.tickmillCompany.name}`}
          yesButtonText='Yes'
          onYes={handleSubscriptionDelete}
          onCancel={handleSubscriptionDeleteModalClose}
          visible={isSubscriptionDeleteModalOpen}
        />
      )}
      <SubscriptionPlatformsTable
        subscriptionDetails={subscriptionDetails}
        onSubscriptionAddModalOpen={handleSubscriptionAddModalOpen}
        onSubscriptionEditModalOpen={handleSubscriptionEditModalOpen}
        onSubscriptionDeleteModalOpen={handleSubscriptionDeleteModalOpen}
      />
    </React.Fragment>
  )
}
